import { useState } from 'react';
import { Download, Calendar, FileText, Users, Clock, TrendingUp, Filter, ChevronRight } from 'lucide-react';

interface Report {
  id: string;
  name: string;
  description: string;
  category: 'scheduling' | 'forecasting' | 'time-attendance' | 'performance';
  icon: React.ReactNode;
  lastGenerated?: Date;
  canDrillDown: boolean;
}

const availableReports: Report[] = [
  {
    id: 'schedule-coverage',
    name: 'Schedule Coverage Report',
    description: 'Analyze staffing coverage vs requirements by hour, day, and channel',
    category: 'scheduling',
    icon: <Calendar className="h-5 w-5" />,
    lastGenerated: new Date(),
    canDrillDown: true,
  },
  {
    id: 'shift-distribution',
    name: 'Shift Distribution Report',
    description: 'View distribution of shifts across teams, agents, and time periods',
    category: 'scheduling',
    icon: <Users className="h-5 w-5" />,
    lastGenerated: new Date(),
    canDrillDown: true,
  },
  {
    id: 'forecast-accuracy',
    name: 'Forecast Accuracy Report',
    description: 'Compare predicted vs actual contact volume with accuracy metrics',
    category: 'forecasting',
    icon: <TrendingUp className="h-5 w-5" />,
    lastGenerated: new Date(),
    canDrillDown: true,
  },
  {
    id: 'timesheet-summary',
    name: 'Timesheet Summary Report',
    description: 'View worked hours, overtime, and pay calculations by agent and period',
    category: 'time-attendance',
    icon: <Clock className="h-5 w-5" />,
    lastGenerated: new Date(),
    canDrillDown: true,
  },
  {
    id: 'exception-analysis',
    name: 'Time Punch Exception Analysis',
    description: 'Analyze patterns in late arrivals, early departures, and missed punches',
    category: 'time-attendance',
    icon: <FileText className="h-5 w-5" />,
    canDrillDown: true,
  },
  {
    id: 'adherence-report',
    name: 'Schedule Adherence Report',
    description: 'Track agent adherence to scheduled activities and identify trends',
    category: 'performance',
    icon: <TrendingUp className="h-5 w-5" />,
    canDrillDown: true,
  },
];

const categories = [
  { value: 'all', label: 'All Categories' },
  { value: 'scheduling', label: 'Scheduling' },
  { value: 'forecasting', label: 'Forecasting' },
  { value: 'time-attendance', label: 'Time & Attendance' },
  { value: 'performance', label: 'Performance' },
];

export default function ReportsListTab() {
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);

  // Filters within each report's drill-down view
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  const [teamFilter, setTeamFilter] = useState<string>('ALL');
  const [channelFilter, setChannelFilter] = useState<string>('ALL');

  const filteredReports = availableReports.filter(
    (report) => categoryFilter === 'all' || report.category === categoryFilter
  );

  const handleOpenReport = (report: Report) => {
    console.log('Opening report:', report.id);
    setSelectedReport(report);
    // Reset filters when opening a new report
    setTeamFilter('ALL');
    setChannelFilter('ALL');
  };

  const handleDownloadReport = (report: Report, format: 'pdf' | 'csv' | 'excel') => {
    console.log('Downloading report:', report.id, format, { dateRange, teamFilter, channelFilter });
    // In production, this would trigger the download with current filters
    alert(`Downloading ${report.name} as ${format.toUpperCase()}...`);
  };

  const teams = ['ALL', 'Technical Support', 'Phone Support', 'Live Chat', 'Email Support'];
  const channels = ['ALL', 'PHONE', 'CHAT', 'EMAIL'];

  if (selectedReport) {
    // Generate demo data based on report type
    const getDemoData = () => {
      switch (selectedReport.id) {
        case 'schedule-coverage':
          return {
            summary: [
              { label: 'Average Coverage', value: '94.2%', trend: '+2.3%', positive: true },
              { label: 'Understaffed Hours', value: '12', trend: '-5 from last week', positive: true },
              { label: 'Overstaffed Hours', value: '8', trend: '+2 from last week', positive: false },
              { label: 'Perfect Coverage', value: '148 hrs', trend: '88% of total', positive: true },
            ],
            tableData: [
              { hour: '6:00 AM', required: 15, actual: 15, coverage: '100%', status: 'Perfect' },
              { hour: '9:00 AM', required: 45, actual: 42, coverage: '93%', status: 'Understaffed' },
              { hour: '12:00 PM', required: 50, actual: 52, coverage: '104%', status: 'Overstaffed' },
              { hour: '3:00 PM', required: 48, actual: 45, coverage: '94%', status: 'Understaffed' },
              { hour: '6:00 PM', required: 32, actual: 32, coverage: '100%', status: 'Perfect' },
              { hour: '9:00 PM', required: 18, actual: 20, coverage: '111%', status: 'Overstaffed' },
            ],
          };
        case 'forecast-accuracy':
          return {
            summary: [
              { label: 'Overall Accuracy', value: '91.8%', trend: '+1.2%', positive: true },
              { label: 'MAPE (Error)', value: '8.2%', trend: '-0.8%', positive: true },
              { label: 'Best Channel', value: 'Phone (95%)', trend: 'Consistent', positive: true },
              { label: 'Needs Improvement', value: 'Chat (85%)', trend: 'Volatile patterns', positive: false },
            ],
            tableData: [
              { channel: 'Phone', predicted: 4832, actual: 4590, variance: '-5.0%', accuracy: '95.0%' },
              { channel: 'Email', predicted: 2000, actual: 2145, variance: '+7.3%', accuracy: '92.7%' },
              { channel: 'Chat', predicted: 3232, actual: 2897, variance: '-10.4%', accuracy: '89.6%' },
              { channel: 'Total', predicted: 10064, actual: 9632, variance: '-4.3%', accuracy: '95.7%' },
            ],
          };
        case 'timesheet-summary':
          return {
            summary: [
              { label: 'Total Hours Worked', value: '6,420', trend: '+128 from last week', positive: true },
              { label: 'Overtime Hours', value: '142', trend: '2.2% of total', positive: false },
              { label: 'Average per Agent', value: '42.8 hrs', trend: 'On target', positive: true },
              { label: 'Total Payroll', value: '$96,300', trend: '+$1,920', positive: false },
            ],
            tableData: [
              { agent: 'Sarah Johnson', regular: 40, overtime: 2, total: 42, pay: '$630' },
              { agent: 'Mike Chen', regular: 40, overtime: 5, total: 45, pay: '$712.50' },
              { agent: 'Emma Davis', regular: 38, overtime: 0, total: 38, pay: '$570' },
              { agent: 'James Wilson', regular: 40, overtime: 3, total: 43, pay: '$667.50' },
              { agent: 'Lisa Martinez', regular: 40, overtime: 0, total: 40, pay: '$600' },
            ],
          };
        case 'exception-analysis':
          return {
            summary: [
              { label: 'Total Exceptions', value: '87', trend: '-12 from last week', positive: true },
              { label: 'Late Clock-ins', value: '32', trend: 'Most common', positive: false },
              { label: 'Missed Punches', value: '18', trend: '-8 from last week', positive: true },
              { label: 'Resolution Rate', value: '94%', trend: '+3%', positive: true },
            ],
            tableData: [
              { type: 'Late Clock-in', count: 32, avgDelay: '8 min', topAgent: 'Mike Chen (5)' },
              { type: 'Early Clock-out', count: 15, avgDelay: '12 min', topAgent: 'Sarah Johnson (3)' },
              { type: 'Missed Punch', count: 18, avgDelay: 'N/A', topAgent: 'Emma Davis (4)' },
              { type: 'Long Break', count: 12, avgDelay: '18 min', topAgent: 'James Wilson (3)' },
              { type: 'Overtime', count: 10, avgDelay: 'N/A', topAgent: 'Mike Chen (4)' },
            ],
          };
        case 'adherence-report':
          return {
            summary: [
              { label: 'Team Adherence', value: '87.5%', trend: '+1.8%', positive: true },
              { label: 'Top Performer', value: 'Sarah J. (98%)', trend: 'Consistent', positive: true },
              { label: 'Needs Coaching', value: '8 agents', trend: '<80% adherence', positive: false },
              { label: 'Avg Non-adherence', value: '22 min/day', trend: '-4 min', positive: true },
            ],
            tableData: [
              { agent: 'Sarah Johnson', adherence: '98.2%', scheduled: '480 min', actual: '471 min', variance: '-9 min' },
              { agent: 'Emma Davis', adherence: '95.8%', scheduled: '480 min', actual: '460 min', variance: '-20 min' },
              { agent: 'Lisa Martinez', adherence: '92.1%', scheduled: '480 min', actual: '442 min', variance: '-38 min' },
              { agent: 'James Wilson', adherence: '88.5%', scheduled: '480 min', actual: '425 min', variance: '-55 min' },
              { agent: 'Mike Chen', adherence: '76.3%', scheduled: '480 min', actual: '366 min', variance: '-114 min' },
            ],
          };
        default:
          return {
            summary: [
              { label: 'Total Records', value: '1,247', trend: '+89', positive: true },
              { label: 'Avg Performance', value: '92.5%', trend: '+2.1%', positive: true },
              { label: 'Top Metric', value: 'Efficiency', trend: '95%', positive: true },
              { label: 'Alert Items', value: '3', trend: 'Requires attention', positive: false },
            ],
            tableData: [
              { category: 'Category A', value: 245, percentage: '45%', status: 'Good' },
              { category: 'Category B', value: 189, percentage: '35%', status: 'Good' },
              { category: 'Category C', value: 108, percentage: '20%', status: 'Needs Review' },
            ],
          };
      }
    };

    const demoData = getDemoData();

    return (
      <div className="space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm">
          <button
            onClick={() => setSelectedReport(null)}
            className="text-purple-600 hover:text-purple-700 font-medium"
          >
            Reports
          </button>
          <ChevronRight className="h-4 w-4 text-gray-400" />
          <span className="text-gray-700">{selectedReport.name}</span>
        </div>

        {/* Report Detail View */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{selectedReport.name}</h2>
              <p className="text-gray-600 mt-1">{selectedReport.description}</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleDownloadReport(selectedReport, 'pdf')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <Download className="h-4 w-4 inline mr-2" />
                PDF
              </button>
              <button
                onClick={() => handleDownloadReport(selectedReport, 'csv')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <Download className="h-4 w-4 inline mr-2" />
                CSV
              </button>
              <button
                onClick={() => handleDownloadReport(selectedReport, 'excel')}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700"
              >
                <Download className="h-4 w-4 inline mr-2" />
                Excel
              </button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              {/* Date Range */}
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Team Filter */}
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <select
                  value={teamFilter}
                  onChange={(e) => setTeamFilter(e.target.value)}
                  className="px-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {teams.map((team) => (
                    <option key={team} value={team}>
                      {team === 'ALL' ? 'All Teams' : team}
                    </option>
                  ))}
                </select>
              </div>

              {/* Channel Filter */}
              <select
                value={channelFilter}
                onChange={(e) => setChannelFilter(e.target.value)}
                className="px-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {channels.map((channel) => (
                  <option key={channel} value={channel}>
                    {channel === 'ALL' ? 'All Channels' : channel}
                  </option>
                ))}
              </select>

              {/* Active filters badge */}
              {(teamFilter !== 'ALL' || channelFilter !== 'ALL') && (
                <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                  Filters active
                </span>
              )}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {demoData.summary.map((item, index) => (
              <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs font-medium text-gray-600 mb-1">{item.label}</p>
                <p className="text-2xl font-bold text-gray-900 mb-1">{item.value}</p>
                <p className={`text-xs ${item.positive ? 'text-green-600' : 'text-amber-600'}`}>
                  {item.trend}
                </p>
              </div>
            ))}
          </div>

          {/* Data Table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {Object.keys(demoData.tableData[0] || {}).map((key) => (
                      <th
                        key={key}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {demoData.tableData.map((row: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50">
                      {Object.values(row).map((value: any, colIndex: number) => (
                        <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Category Filter */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredReports.map((report) => (
          <div
            key={report.id}
            onClick={() => handleOpenReport(report)}
            className="bg-white border border-gray-200 rounded-lg p-6 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer group"
          >
            {/* Icon and Category Badge */}
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-lg text-purple-600 group-hover:bg-purple-200 transition-colors">
                {report.icon}
              </div>
              <span className="px-2 py-1 text-xs font-medium text-purple-700 bg-purple-50 rounded-full">
                {categories.find((c) => c.value === report.category)?.label}
              </span>
            </div>

            {/* Report Info */}
            <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-purple-700 transition-colors">
              {report.name}
            </h3>
            <p className="text-sm text-gray-600 mb-4">{report.description}</p>

            {report.lastGenerated && (
              <p className="text-xs text-gray-500 mb-4">
                Last run: {report.lastGenerated.toLocaleDateString()}
              </p>
            )}

            {/* Click to view indicator */}
            <div className="flex items-center text-sm text-purple-600 font-medium">
              <span>Click to view report</span>
              <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        ))}
      </div>

      {filteredReports.length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No reports found</h3>
          <p className="text-gray-600">Try changing the category filter</p>
        </div>
      )}
    </div>
  );
}
